import {
  RuntimeRequestError,
  RuntimeTransportStoppedError,
  type RuntimeCloseInfo,
  type RuntimeConnectionSnapshot,
  type RuntimeEventFrame,
  type RuntimeTransport,
  type RuntimeTransportLifecycleOptions,
} from "./runtime-transport";

type ElectronRuntimeEventFrame = RuntimeEventFrame & {
  instanceId: string;
};

type ElectronRuntimeCloseInfo = RuntimeCloseInfo & {
  instanceId: string;
};

type ElectronRuntimeBridge = {
  runtimeTransportStart: NonNullable<ElectronAPI["runtimeTransportStart"]>;
  runtimeTransportStop: NonNullable<ElectronAPI["runtimeTransportStop"]>;
  runtimeTransportRequest: NonNullable<ElectronAPI["runtimeTransportRequest"]>;
  runtimeTransportSnapshot: NonNullable<ElectronAPI["runtimeTransportSnapshot"]>;
  onRuntimeTransportEvent: NonNullable<ElectronAPI["onRuntimeTransportEvent"]>;
  onRuntimeTransportClose: NonNullable<ElectronAPI["onRuntimeTransportClose"]>;
};

export type ElectronRuntimeTransportOptions = RuntimeTransportLifecycleOptions & {
  instanceId: string;
};

function getElectronRuntimeBridge(): ElectronRuntimeBridge | null {
  if (typeof window === "undefined") {
    return null;
  }
  const api = window.electronAPI;
  if (
    !api ||
    typeof api.runtimeTransportStart !== "function" ||
    typeof api.runtimeTransportStop !== "function" ||
    typeof api.runtimeTransportRequest !== "function" ||
    typeof api.runtimeTransportSnapshot !== "function" ||
    typeof api.onRuntimeTransportEvent !== "function" ||
    typeof api.onRuntimeTransportClose !== "function"
  ) {
    return null;
  }
  return {
    runtimeTransportStart: api.runtimeTransportStart,
    runtimeTransportStop: api.runtimeTransportStop,
    runtimeTransportRequest: api.runtimeTransportRequest,
    runtimeTransportSnapshot: api.runtimeTransportSnapshot,
    onRuntimeTransportEvent: api.onRuntimeTransportEvent,
    onRuntimeTransportClose: api.onRuntimeTransportClose,
  };
}

export function canUseElectronRuntimeTransport(): boolean {
  return getElectronRuntimeBridge() !== null;
}

function normalizeRuntimeBridgeError(error: unknown): Error {
  if (error instanceof RuntimeTransportStoppedError || error instanceof RuntimeRequestError) {
    return error;
  }
  if (error instanceof Error) {
    return new RuntimeRequestError("runtime_ipc_error", error.message);
  }
  return new RuntimeRequestError("runtime_ipc_error", String(error));
}

class ElectronRuntimeTransport implements RuntimeTransport {
  private readonly bridge: ElectronRuntimeBridge;
  private readonly options: ElectronRuntimeTransportOptions;
  private pendingRequestCount = 0;
  private readyState: RuntimeConnectionSnapshot["readyState"] = "closed";
  private pendingConnectError: RuntimeConnectionSnapshot["pendingConnectError"];
  private lastSeq: number | null = null;
  private offEvent: (() => void) | null = null;
  private offClose: (() => void) | null = null;
  private stopped = false;

  constructor(bridge: ElectronRuntimeBridge, options: ElectronRuntimeTransportOptions) {
    this.bridge = bridge;
    this.options = options;
  }

  start(): void {
    if (this.readyState === "connecting" || this.readyState === "open") {
      return;
    }
    this.stopped = false;
    this.readyState = "connecting";
    this.offEvent = this.bridge.onRuntimeTransportEvent((event) => {
      if (event.instanceId !== this.options.instanceId) {
        return;
      }
      if (typeof event.seq === "number") {
        if (this.lastSeq !== null && event.seq !== this.lastSeq + 1) {
          this.options.onGap?.({ expected: this.lastSeq + 1, received: event.seq });
        }
        this.lastSeq = event.seq;
      }
      this.options.onEvent?.(event);
    });
    this.offClose = this.bridge.onRuntimeTransportClose((info) => {
      if (info.instanceId !== this.options.instanceId) {
        return;
      }
      this.handleClose(info);
    });
    void this.bridge
      .runtimeTransportStart({
        clientName: this.options.clientName,
        clientVersion: this.options.clientVersion,
        platform: this.options.platform,
        mode: this.options.mode,
        instanceId: this.options.instanceId,
      })
      .then((result) => {
        if (this.stopped) {
          return;
        }
        if (!result.success) {
          this.handleClose({
            instanceId: this.options.instanceId,
            code: 1006,
            reason: result.error?.message ?? "Runtime IPC start failed.",
            error: result.error,
          });
          return;
        }
        this.pendingConnectError = undefined;
        this.readyState = "open";
        this.options.onHello?.(result.hello ?? null);
      })
      .catch((error) => {
        if (this.stopped) {
          return;
        }
        const normalized = normalizeRuntimeBridgeError(error);
        this.handleClose({
          instanceId: this.options.instanceId,
          code: 1006,
          reason: normalized.message,
          error: {
            code: normalized instanceof RuntimeRequestError
              ? normalized.runtimeCode
              : "runtime_ipc_error",
            message: normalized.message,
          },
        });
      });
  }

  stop(): void {
    this.stopped = true;
    this.readyState = "closing";
    this.offEvent?.();
    this.offClose?.();
    this.offEvent = null;
    this.offClose = null;
    void this.bridge.runtimeTransportStop(this.options.instanceId).finally(() => {
      this.readyState = "closed";
    });
  }

  getConnectionSnapshot(): RuntimeConnectionSnapshot {
    return {
      readyState: this.readyState,
      url: "electron-ipc://runtime",
      hasToken: false,
      pendingRequestCount: this.pendingRequestCount,
      lastSeq: this.lastSeq,
      pendingConnectError: this.pendingConnectError,
    };
  }

  async request<T = unknown>(method: string, params?: unknown): Promise<T> {
    if (this.stopped || this.readyState === "closed" || this.readyState === "closing") {
      throw new RuntimeTransportStoppedError();
    }
    this.pendingRequestCount += 1;
    try {
      const result = await this.bridge.runtimeTransportRequest<T>({
        instanceId: this.options.instanceId,
        method,
        payload: params,
      });
      if (!result.success) {
        throw new RuntimeRequestError(
          result.error?.code ?? "runtime_request_failed",
          result.error?.message ?? "Runtime request failed.",
          result.error?.details,
        );
      }
      return result.payload as T;
    } catch (error) {
      throw normalizeRuntimeBridgeError(error);
    } finally {
      this.pendingRequestCount = Math.max(0, this.pendingRequestCount - 1);
    }
  }

  private handleClose(info: ElectronRuntimeCloseInfo): void {
    this.readyState = "closed";
    this.pendingConnectError = info.error;
    this.offEvent?.();
    this.offClose?.();
    this.offEvent = null;
    this.offClose = null;
    this.options.onClose?.({
      code: info.code,
      reason: info.reason,
      error: info.error,
    });
  }
}

export function createElectronRuntimeTransport(
  options: ElectronRuntimeTransportOptions,
): RuntimeTransport | null {
  const bridge = getElectronRuntimeBridge();
  if (!bridge) {
    return null;
  }
  return new ElectronRuntimeTransport(bridge, options);
}
